import React from 'react';
import {Badge, Button, Card, Form, Table} from 'react-bootstrap';
import {useSelector} from 'react-redux';
import {getAdminInvoiceRequests, updateAdminInvoiceRequest} from '../api';
import {useTranslation} from 'react-i18next';

const euros = value => value == null ? '0.00' : (Number(value) / 100).toFixed(2);

const statusKeys = {requested: 'INVOICE_TO_ISSUE', issued: 'INVOICE_ISSUED', cancelled: 'INVOICE_CANCELLED'};

const statusVariants = {
  requested: 'warning',
  issued: 'success',
  cancelled: 'secondary'
};

const fiscalSummary = data => {
  if (!data) return '-';
  if (data.customerType === 'business') {
    return [
      data.businessName,
      data.vatNumber ? `P.IVA ${data.vatNumber}` : null,
      data.sdiPec ? `SDI/PEC ${data.sdiPec}` : null
    ].filter(Boolean).join(' · ') || '-';
  }
  return [
    [data.name, data.surname].filter(Boolean).join(' '),
    data.fiscalCode ? `CF ${data.fiscalCode}` : null
  ].filter(Boolean).join(' · ') || '-';
};

const AdminInvoiceRequests = () => {
  const {t, i18n} = useTranslation();
  const statusLabel = value => statusKeys[value] ? t(statusKeys[value]) : value;
  const formatDate = value => value ? new Date(value).toLocaleString(i18n.language) : '-';
  const token = useSelector(state => state.login && state.login.token);
  const [requests, setRequests] = React.useState([]);
  const [status, setStatus] = React.useState('requested');
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [updatingId, setUpdatingId] = React.useState(null);
  const [message, setMessage] = React.useState('');

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const response = await getAdminInvoiceRequests(token, {status, email});
    if (response.status === 200) {
      setRequests(Array.isArray(response.json) ? response.json : []);
      setMessage('');
    } else {
      setMessage(response.json.error || t('INVOICE_REQUESTS_LOAD_ERROR'));
    }
    setLoading(false);
  }, [token, status, email, t]);

  React.useEffect(() => { load(); }, [load]);

  const changeStatus = async (transactionId, nextStatus) => {
    setUpdatingId(transactionId);
    const response = await updateAdminInvoiceRequest(token, transactionId, nextStatus);
    if (response.status === 200) {
      setRequests(current => current.map(item => (
        item.transactionId === transactionId ? response.json : item
      )));
      setMessage(t('INVOICE_REQUEST_UPDATED'));
    } else {
      setMessage(response.json.error || t('INVOICE_REQUEST_UPDATE_ERROR'));
    }
    setUpdatingId(null);
  };

  return (
    <Card className="partner-report-skywork-shop">
      <Card.Body>
        <div className="partner-report-skywork-shop-head">
          <div>
            <span className="partner-report-skywork-card-icon"><i className="fa fa-file-text-o"/></span>
            <div>
              <h4>{t('INVOICE_REQUESTS')}</h4>
              <p>{t('DISPLAYED_REQUEST_COUNT', {count: requests.length})}</p>
            </div>
          </div>
          <div className="partner-report-skywork-shop-filters">
            <Form.Control
              as="select"
              size="sm"
              className="mr-2 mb-2"
              value={status}
              onChange={event => setStatus(event.target.value)}
            >
              <option value="">{t('ALL_STATUSES')}</option>
              {Object.keys(statusKeys).map(value => (
                <option key={value} value={value}>{statusLabel(value)}</option>
              ))}
            </Form.Control>
            <Form.Control
              size="sm"
              className="mr-2 mb-2"
              placeholder={t('SEARCH_EMAIL')}
              value={email}
              onChange={event => setEmail(event.target.value)}
            />
            <Button size="sm" className="partner-report-skywork-secondary" onClick={load} disabled={loading}>
              <i className={`fa ${loading ? 'fa-circle-o-notch fa-spin' : 'fa-refresh'}`}/> {loading ? t('LOADING') : t('UPDATE')}
            </Button>
          </div>
        </div>
        {message && <div className="alert alert-info">{message}</div>}
        <div className="partner-report-skywork-table-wrap">
          <Table responsive hover className="partner-report-skywork-table">
            <thead>
              <tr>
                <th>{t('TRANSACTION')}</th>
                <th>{t('CUSTOMER')}</th>
                <th>{t('FISCAL_DATA')}</th>
                <th>{t('PURCHASE')}</th>
                <th>{t('SHOP_CLUB')}</th>
                <th>{t('AMOUNT')}</th>
                <th>{t('STATE')}</th>
                <th>{t('ACTIONS')}</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(item => (
                <tr key={item.transactionId}>
                  <td><strong>#{item.transactionId}</strong><br/><small>{formatDate(item.date)}</small></td>
                  <td>{item.customerName || '-'}<br/><small>{item.userEmail}</small></td>
                  <td>{fiscalSummary(item.invoiceData)}<br/><small>{[item.invoiceData.address || item.invoiceData.registeredOffice, item.invoiceData.postalCode, item.invoiceData.city, item.invoiceData.province].filter(Boolean).join(' ')}</small></td>
                  <td>{item.causeLabel || item.cause}<br/><small>{item.bookingId ? t('BOOKING_NUMBER', {id: item.bookingId}) : item.videoPartnerId ? t('PARTNER_VIDEO_NUMBER', {id: item.videoPartnerId}) : ''}</small></td>
                  <td>{item.partnerName || 'Replayer'}</td>
                  <td>EUR {euros(Math.abs(Number(item.amount || 0)))}</td>
                  <td><Badge variant={statusVariants[item.invoiceStatus] || 'secondary'}>{statusLabel(item.invoiceStatus)}</Badge></td>
                  <td>
                    <Button size="sm" variant="outline-success" className="mr-2 mb-1" disabled={updatingId === item.transactionId || item.invoiceStatus === 'issued'} onClick={() => changeStatus(item.transactionId, 'issued')}>
                      {t('INVOICE_ISSUED')}
                    </Button>
                    <Button size="sm" variant="outline-secondary" className="mb-1" disabled={updatingId === item.transactionId || item.invoiceStatus === 'cancelled'} onClick={() => changeStatus(item.transactionId, 'cancelled')}>
                      {t('CANCEL')}
                    </Button>
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr><td colSpan={8}>{t('NO_INVOICE_REQUESTS')}</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default AdminInvoiceRequests;
